import React, { useState, useEffect } from "react";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaWhatsapp,
} from "react-icons/fa";
import { toast } from "react-hot-toast";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${SERVER_URL}/api/contact/send-mail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(data.success || "Message sent successfully!");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        toast.error(data.error || "Couldn't send message. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black px-6 py-20 mt-16 font-sans">
      <Helmet>
        <title>Contact Us | Pak Fashions</title>
        <meta
          name="description"
          content="Get in touch with Pak Fashions for inquiries, support, or feedback. We're here to help you on your sustainable fashion journey."
        />
        <meta
          name="keywords"
          content="contact Pak Fashions, get in touch, fashion help, customer support, Laikipia, Nanyuki, Kenya"
        />
        <meta name="author" content="Pak Fashions" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Contact Us | Pak Fashions" />
        <meta
          property="og:description"
          content="Reach out to Pak Fashions via email, phone, WhatsApp, or visit our location in Nanyuki."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pakfashions.co.ke/contact" />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/pak-fashions.jpg"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | Pak Fashions" />
        <meta
          name="twitter:description"
          content="Reach out to Pak Fashions via email, phone, WhatsApp, or visit our location in Nanyuki."
        />
        <meta
          name="twitter:image"
          content="https://pakfashions.co.ke/pak-fashions.jpg"
        />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Pak Fashions",
            url: "https://pakfashions.co.ke",
            email: "pakfashionske@gmail.com",
            telephone: "+254 724 835785",
            address: {
              "@type": "PostalAddress",
              streetAddress: "237G+MQ6, Nanyuki",
              addressLocality: "Nanyuki",
              addressRegion: "Laikipia",
              addressCountry: "KE",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+254 724 835785",
              contactType: "Customer Support",
              areaServed: "KE",
              availableLanguage: "en",
            },
            sameAs: [
              "https://wa.me/2724835785",
              "https://web.facebook.com/PAKFASHIONSKE",
              "https://www.instagram.com/pakfashionske?igsh=MXZsbXd3YnRhamltYg==",
            ],
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="text-center mb-24" data-aos="fade-down">
        <h1 className="text-5xl md:text-6xl font-extrabold mb-6 tracking-tight text-gray-900">
          Contact <span className="text-yellow-500">Pak Fashions</span>
        </h1>
        <p className="text-lg md:text-xl max-w-3xl mx-auto text-gray-500 leading-relaxed font-medium">
          Questions? Feedback? We're always here to help with your sustainable
          fashion journey.
        </p>
        <div className="w-24 h-1 bg-yellow-400 mx-auto mt-8 rounded-full shadow-sm" />
      </div>

      {/* Info & Form Section */}
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 lg:gap-24 items-start">
        {/* Info Cards */}
        <div className="space-y-8" data-aos="fade-right">
          <h2 className="text-3xl font-bold border-l-4 border-yellow-400 pl-5 mb-8 text-gray-900 tracking-tight">
            Reach Out To Us
          </h2>

          <div className="group bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex items-start gap-6 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-yellow-400 transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
            <div className="w-14 h-14 bg-yellow-50 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-yellow-400 transition-colors duration-500">
              <FaEnvelope className="text-2xl text-yellow-600 group-hover:text-white transition-colors duration-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Email</h3>
              <a
                href="mailto:pakfashionske@gmail.com"
                className="text-gray-500 hover:text-yellow-600 hover:underline transition-colors break-all text-lg"
              >
                pakfashionske@gmail.com
              </a>
            </div>
          </div>

          <div className="group bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex items-start gap-6 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-yellow-400 transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
            <div className="w-14 h-14 bg-yellow-50 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-yellow-400 transition-colors duration-500">
              <FaPhoneAlt className="text-2xl text-yellow-600 group-hover:text-white transition-colors duration-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Phone</h3>
              <div className="flex flex-col gap-1 text-lg">
                <a
                  href="tel:+254724835785"
                  className="text-gray-500 hover:text-yellow-600 hover:underline transition-colors"
                >
                  +254 724 835 785
                </a>
                <a
                  href="tel:+254720146386"
                  className="text-gray-500 hover:text-yellow-600 hover:underline transition-colors"
                >
                  +254 720 146 386
                </a>
              </div>
            </div>
          </div>

          <div className="group bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex items-start gap-6 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-yellow-400 transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
            <div className="w-14 h-14 bg-yellow-50 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-yellow-400 transition-colors duration-500">
              <FaMapMarkerAlt className="text-2xl text-yellow-600 group-hover:text-white transition-colors duration-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Visit Us</h3>
              <p className="text-gray-500 text-lg leading-relaxed">
                Pak Fashions Clothes, Pak Fashions 1 at Juba House and Pak
                Fashions 2 at Napolitana building, Nanyuki
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div data-aos="fade-left" className="relative group/form">
          <div className="absolute -inset-4 bg-yellow-50 rounded-[3rem] opacity-50 group-hover/form:opacity-100 transition-all duration-500 blur-xl"></div>
          <form
            onSubmit={handleSubmit}
            className="relative bg-white rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 p-10 md:p-12 space-y-6"
          >
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8 tracking-tight">
              Send Us a Message
            </h2>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-2 pl-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-2 pl-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-2 pl-1">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="How can we help?"
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-2 pl-1">
                  Your Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write your message here..."
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 h-40 resize-none focus:outline-none focus:bg-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 hover:bg-black text-white py-4 rounded-2xl font-bold tracking-wide transition-all duration-300 shadow-lg shadow-gray-200 hover:-translate-y-0.5 mt-4 disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                  Sending...
                </span>
              ) : (
                "Send Message"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Google Map */}
      <section className="mt-32 max-w-6xl mx-auto" data-aos="fade-up">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
            Find Us on the Map
          </h2>
          <div className="w-16 h-1 bg-yellow-400 mx-auto mt-4 rounded-full shadow-sm" />
        </div>
        <div className="w-full h-[400px] rounded-[3rem] shadow-xl overflow-hidden border-8 border-white group">
          <iframe
            title="Pak Fashion Location"
            src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d15959.273034144248!2d37.0698452!3d0.0100851!3m2!1i1024!2i768!4f13.1!2m1!1spak%20fashion%20nanyuki!5e0!3m2!1sen!2ske!4v1748244256144!5m2!1sen!2ske"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            className="filter grayscale group-hover:grayscale-0 transition-all duration-1000"
          />
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/254724835785?text=Hello!%20I%20have%20a%20question%20about%20pakfashions%20products."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 bg-[#25D366] text-white p-4 rounded-full shadow-[0_8px_30px_rgb(37,211,102,0.4)] hover:bg-[#1EBE5D] hover:scale-110 transition-all duration-300 z-50 animate-bounce cursor-pointer flex items-center justify-center group"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp className="text-3xl group-hover:rotate-12 transition-transform" />
      </a>
    </div>
  );
}

export default Contact;
